import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import authService from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import adminService from '../../services/adminService';
import reviewsService from '../../services/reviewsService';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Badge,
  CircularProgress,
  alpha,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Luggage as TripsIcon,
  BookOnline as BookingsIcon,
  Explore as PlanRequestsIcon,
  Payment as PaymentsIcon,
  RateReview as ReviewsIcon,
  ContactMail as ContactIcon,
  Email as NewsletterIcon,
  People as UsersIcon,
  BarChart as AnalyticsIcon,
  BuildCircle as BEUXIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  History as AuditIcon,
  MonitorHeart as SystemIcon,
  Campaign as BroadcastIcon,
} from '@mui/icons-material';
import logo from '../../logos/logo-transparent.png';
import CommandPalette from './CommandPalette';

const drawerWidth = 280;

const AdminLayout = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState(null);
  const [counts, setCounts] = useState({ bookings: 0, reviews: 0, contacts: 0, planRequests: 0 });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Verify auth before showing admin content - redirect to login if not authenticated or not admin
  useEffect(() => {
    const checkAuth = async () => {
      if (!authService.isAuthenticated()) {
        navigate('/login', { replace: true });
        return;
      }
      try {
        await authService.getCurrentUser();
        if (!authService.isAdmin()) {
          authService.logout();
          navigate('/login', { replace: true });
          return;
        }
        setUser(authService.getStoredUser());
      } catch {
        authService.logout();
        navigate('/login', { replace: true });
        return;
      }
      setAuthReady(true);
    };
    checkAuth();
  }, [navigate]);

  // Refresh user when profile is updated (e.g. profile picture upload)
  useEffect(() => {
    const handleProfileUpdated = () => setUser(authService.getStoredUser());
    window.addEventListener('profile-updated', handleProfileUpdated);
    return () => window.removeEventListener('profile-updated', handleProfileUpdated);
  }, []);

  useEffect(() => {
    if (!authReady) return;
    const fetchCounts = async () => {
      try {
        const [dashboardRes, reviewsRes, planReqRes] = await Promise.all([
          adminService.getDashboardStats(),
          reviewsService.getAllReviews({ per_page: 1 }),
          adminService.getPlanRequests({ status: 'pending', per_page: 1 }).catch(() => null),
        ]);
        const stats = dashboardRes?.stats ?? dashboardRes;
        const reviewsTotal = reviewsRes?.pagination?.total_count ?? reviewsRes?.pagination?.total ?? 0;
        const planRequestsTotal = planReqRes?.pagination?.total_count ?? planReqRes?.pagination?.total ?? 0;
        setCounts({
          bookings: stats?.bookings?.total ?? 0,
          reviews: reviewsTotal,
          contacts: stats?.contacts?.total ?? 0,
          planRequests: planRequestsTotal,
        });
      } catch {
        setCounts({ bookings: 0, reviews: 0, contacts: 0, planRequests: 0 });
      }
    };
    fetchCounts();
  }, [authReady]);

  const isSuperAdmin = (user?.role || authService.getStoredUser()?.role) === 'super_admin';

  // Grouped navigation for a holistic ops console. superAdmin-only items are gated.
  const navGroups = [
    {
      heading: 'Overview',
      items: [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin' },
        { text: 'Analytics', icon: <AnalyticsIcon />, path: '/admin/analytics' },
      ],
    },
    {
      heading: 'Commerce',
      items: [
        { text: 'Bookings', icon: <BookingsIcon />, path: '/admin/bookings', badge: counts.bookings },
        { text: 'Trip Requests', icon: <PlanRequestsIcon />, path: '/admin/plan-requests', badge: counts.planRequests },
        { text: 'Payments', icon: <PaymentsIcon />, path: '/admin/payments' },
      ],
    },
    {
      heading: 'Content',
      items: [
        { text: 'Trips', icon: <TripsIcon />, path: '/admin/trips' },
        { text: 'Reviews', icon: <ReviewsIcon />, path: '/admin/reviews', badge: counts.reviews },
        { text: 'BE-UX', icon: <BEUXIcon />, path: '/admin/be-ux' },
      ],
    },
    {
      heading: 'Audience',
      items: [
        { text: 'Users', icon: <UsersIcon />, path: '/admin/users' },
        { text: 'Contact', icon: <ContactIcon />, path: '/admin/contacts', badge: counts.contacts },
        { text: 'Newsletter', icon: <NewsletterIcon />, path: '/admin/newsletter' },
        { text: 'Broadcast', icon: <BroadcastIcon />, path: '/admin/broadcast', superAdmin: true },
      ],
    },
    {
      heading: 'System',
      items: [
        { text: 'Audit Log', icon: <AuditIcon />, path: '/admin/audit' },
        { text: 'System', icon: <SystemIcon />, path: '/admin/system' },
        { text: 'Settings', icon: <SettingsIcon />, path: '/admin/settings' },
      ],
    },
  ];
  const allItems = navGroups.flatMap((g) => g.items);

  const MUTED = alpha('#ffffff', 0.66);
  const navButton = (item) => {
    if (item.superAdmin && !isSuperAdmin) return null;
    const isActive = location.pathname === item.path || (item.path !== '/admin' && location.pathname.startsWith(item.path));
    return (
      <ListItem key={item.text} disablePadding sx={{ mb: 0.25 }}>
        <ListItemButton
          onClick={() => { navigate(item.path); if (isMobile) setMobileOpen(false); }}
          sx={{
            borderRadius: 2.5, px: 1.5, py: 0.85,
            color: isActive ? '#fff' : MUTED,
            bgcolor: isActive ? theme.palette.primary.main : 'transparent',
            boxShadow: isActive ? `0 8px 20px ${alpha(theme.palette.primary.main, 0.45)}` : 'none',
            '&:hover': { bgcolor: isActive ? theme.palette.primary.dark : alpha('#fff', 0.07), color: '#fff' },
            transition: 'all 0.2s ease',
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
            {item.badge != null && item.badge > 0 ? (
              <Badge badgeContent={item.badge} color="error">{item.icon}</Badge>
            ) : item.icon}
          </ListItemIcon>
          <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: isActive ? 700 : 500, fontSize: '0.88rem' }} />
        </ListItemButton>
      </ListItem>
    );
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#1c1206', color: '#fff' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 2.25, cursor: 'pointer' }} onClick={() => navigate('/admin')}>
        <Box component="img" src={logo} alt="Royal Dastinos" sx={{ height: 34, filter: 'brightness(1.1)' }} />
        <Box>
          <Typography sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: '#fff', fontSize: '1.05rem', lineHeight: 1 }}>
            Royal Dastinos
          </Typography>
          <Typography sx={{ color: '#FF7420', fontSize: '0.68rem', fontWeight: 700, letterSpacing: 1.5 }}>
            {isSuperAdmin ? 'SUPER ADMIN' : 'ADMIN CONSOLE'}
          </Typography>
        </Box>
      </Box>

      {/* ⌘K search trigger */}
      <Box sx={{ px: 2, mb: 1 }}>
        <Box
          onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: 2.5, cursor: 'pointer',
            bgcolor: alpha('#fff', 0.06), border: `1px solid ${alpha('#fff', 0.1)}`, color: MUTED,
            '&:hover': { bgcolor: alpha('#fff', 0.1) },
          }}
        >
          <SearchIcon sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: '0.82rem', flex: 1 }}>Search anything…</Typography>
          <Chip label="⌘K" size="small" sx={{ height: 20, fontSize: '0.62rem', bgcolor: alpha('#fff', 0.12), color: MUTED }} />
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pb: 1 }}>
        {navGroups.map((group) => {
          const visible = group.items.filter((it) => !it.superAdmin || isSuperAdmin);
          if (visible.length === 0) return null;
          return (
            <Box key={group.heading} sx={{ mb: 1.5 }}>
              <Typography sx={{ px: 1.5, py: 0.5, color: alpha('#fff', 0.4), fontSize: '0.66rem', fontWeight: 700, letterSpacing: 1.2 }}>
                {group.heading.toUpperCase()}
              </Typography>
              <List disablePadding>{group.items.map(navButton)}</List>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ px: 1.5, pb: 2 }}>
        <Divider sx={{ borderColor: alpha('#fff', 0.09), mb: 1 }} />
        <ListItemButton onClick={() => navigate('/')} sx={{ borderRadius: 2.5, px: 1.5, py: 0.75, color: MUTED, '&:hover': { bgcolor: alpha('#fff', 0.07), color: '#fff' } }}>
          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}><TripsIcon /></ListItemIcon>
          <ListItemText primary="View site" primaryTypographyProps={{ fontSize: '0.85rem' }} />
        </ListItemButton>
        <ListItemButton onClick={handleLogout} sx={{ borderRadius: 2.5, px: 1.5, py: 0.75, color: alpha('#ff8a80', 0.9), '&:hover': { bgcolor: alpha('#ff5252', 0.12), color: '#ff8a80' } }}>
          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}><LogoutIcon /></ListItemIcon>
          <ListItemText primary="Log out" primaryTypographyProps={{ fontSize: '0.85rem' }} />
        </ListItemButton>
      </Box>
    </Box>
  );

  // Don't render admin UI until auth is verified - prevents reaching admin without logging in
  if (!authReady) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CommandPalette />
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'white',
          color: theme.palette.text.primary,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 700, fontFamily: '"Playfair Display", serif' }}>
            {[...allItems].find(item => item.path === location.pathname)?.text || 'Admin Panel'}
          </Typography>

          <Tooltip title="Search (⌘K)">
            <IconButton onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))}>
              <SearchIcon />
            </IconButton>
          </Tooltip>

          <IconButton
            onClick={handleProfileMenuOpen}
            sx={{ ml: 1 }}
          >
            <Avatar 
              src={user?.profile_picture_url ?? user?.profile_picture ?? user?.profilePicture}
              sx={{ 
                width: 36, 
                height: 36, 
                bgcolor: theme.palette.primary.main 
              }}
            >
              {user ? ((user.first_name || user.firstName || '').charAt(0) || (user.last_name || user.lastName || '').charAt(0) || (user.email || '?').charAt(0).toUpperCase()) : '?'}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: { width: 220, mt: 1 }
        }}
      >
        <MenuItem onClick={() => { navigate('/admin/profile'); handleProfileMenuClose(); }}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>My Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { navigate('/admin/settings'); handleProfileMenuClose(); }}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>
            <Typography color="error">Logout</Typography>
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: '#1c1206',
              border: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: '#1c1206',
              border: 'none',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          bgcolor: theme.palette.background.default,
          minHeight: '100vh',
          mt: { xs: '56px', sm: '64px' }
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminLayout;


